import { FirebaseClient } from '@/api/firebaseClient';
import { ownerTicketTypesQuery, QUERY_KEYS } from '@/api/queries';
import { useInvalidatingMutation } from '@/hooks/useInvalidatingMutation';
import { toast } from '@/components';
import {
  BookingField,
  PlaceTicketOrderRequest,
  TicketingOwnerRef,
  TicketType,
} from '@packages/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deriveBuyerEmail, firstTicketEmail, nextTicketOrderState } from './ticketOrderEmail';

export type TicketingOwnerContext = {
  ownerInput: PlaceTicketOrderRequest['owner'];
  ownerRef: TicketingOwnerRef;
  title: string;
  subtitle?: string;
  saleState: string;
  bookingFields?: BookingField[];
  /** Service fee (%) for this owner; falls back to DEFAULT_SERVICE_FEE_PERCENT. */
  serviceFeePercent?: number;
  /** Where to navigate after a successful order. Defaults to `/orders/:id`
   * (festival); event microsites pass the nested `/events/:slug/orders/:id`. */
  orderPath?: (orderId: string) => string;
  /** Event microsites use the first attendee email as the buyer/order email by default. */
  syncBuyerEmailWithFirstTicket?: boolean;
};

export type TicketItem = {
  rowId: string;
  ticketTypeId: string;
  name: string;
  email: string;
  customFields: Record<string, string>;
};

const client = new FirebaseClient();

/** Default service fee (%) when an owner has none configured (matches `getPaymentUrl`). */
export const DEFAULT_SERVICE_FEE_PERCENT = 5;

export const activeFields = (fields: BookingField[] = [], appliesTo: 'order' | 'attendee') =>
  fields
    .filter((field) => !field.archivedAt && field.appliesTo === appliesTo)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

export const fieldValue = (values: Record<string, string>, field: BookingField) =>
  values[field.key] ?? '';

/**
 * Shared ticket-purchase logic for both the festival and event microsite shells.
 * Holds the multi-ticket cart (1–5), order-level buyer, per-attendee booking
 * fields, live total and the Stripe-bound submit. The two presentational shells
 * (festival collage / event themed) consume this hook and render the same data
 * in their own layouts. `formId` must wrap all inputs + the submit button so the
 * native required-field validation scrolls to the first missing field.
 */
export const useTicketOrder = (context: TicketingOwnerContext) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ticketsRef = useRef<HTMLDivElement>(null);
  const previousFirstTicketEmailRef = useRef<string | undefined>(undefined);
  const [buyerEmail, setBuyerEmail] = useState('');
  const [buyerFields, setBuyerFields] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [tickets, setTickets] = useState<TicketItem[]>([]);

  const { data: ticketTypes = [], isLoading } = useQuery(ownerTicketTypesQuery(context.ownerRef));

  const orderFields = useMemo(
    () => activeFields(context.bookingFields, 'order'),
    [context.bookingFields],
  );
  const attendeeFields = useMemo(
    () => activeFields(context.bookingFields, 'attendee'),
    [context.bookingFields],
  );

  const { mutateAsync: placeTicketOrder, isPending } = useInvalidatingMutation(
    [QUERY_KEYS.ORDERS],
    { mutationFn: async (data: PlaceTicketOrderRequest) => client.placeTicketOrder(data) },
  );

  useEffect(() => {
    if (!context.syncBuyerEmailWithFirstTicket) return;

    const previousFirstTicketEmail = previousFirstTicketEmailRef.current;
    previousFirstTicketEmailRef.current = firstTicketEmail(tickets);

    setBuyerEmail((currentBuyerEmail) => {
      return nextTicketOrderState({
        tickets,
        buyerEmail: currentBuyerEmail,
        previousFirstTicketEmail,
      }).buyerEmail;
    });
  }, [context.syncBuyerEmailWithFirstTicket, tickets]);

  const ticketsWithType = useMemo(
    () =>
      tickets.map((ticket) => ({
        ticket,
        ticketType: ticketTypes.find((type) => type.ticketTypeId === ticket.ticketTypeId),
      })),
    [ticketTypes, tickets],
  );

  const totalPrice = useMemo(
    () => ticketsWithType.reduce((acc, item) => acc + (item.ticketType?.price ?? 0), 0),
    [ticketsWithType],
  );

  /**
   * Order summary, mirroring the Stripe checkout breakdown: one line per ticket
   * type (count × label → line total), a 5% service fee (same flat rate the
   * `getPaymentUrl` function applies), and the grand total.
   */
  const summary = useMemo(() => {
    const groups = new Map<
      string,
      { ticketTypeId: string; label: string; unitPrice: number; count: number; lineTotal: number }
    >();
    for (const { ticket, ticketType } of ticketsWithType) {
      const id = ticket.ticketTypeId;
      const unitPrice = ticketType?.price ?? 0;
      const existing = groups.get(id);
      if (existing) {
        existing.count += 1;
        existing.lineTotal += unitPrice;
      } else {
        groups.set(id, {
          ticketTypeId: id,
          label: ticketType?.label ?? '',
          unitPrice,
          count: 1,
          lineTotal: unitPrice,
        });
      }
    }
    const subtotal = totalPrice;
    const serviceFeePercent = context.serviceFeePercent ?? DEFAULT_SERVICE_FEE_PERCENT;
    const serviceFee = Math.round(subtotal * (serviceFeePercent / 100) * 100) / 100;
    const grandTotal = Math.round((subtotal + serviceFee) * 100) / 100;
    return { lineItems: [...groups.values()], subtotal, serviceFeePercent, serviceFee, grandTotal };
  }, [ticketsWithType, totalPrice, context.serviceFeePercent]);

  const countForType = (type: TicketType) =>
    tickets.filter((t) => t.ticketTypeId === type.ticketTypeId).length;

  const availableForType = (type: TicketType) => {
    if (typeof type.available !== 'number') return true;
    return type.available - countForType(type) > 0;
  };

  /** A cart line is incomplete when a required attendee value is empty. */
  const ticketIncomplete = (ticket: TicketItem) => {
    if (!ticket.name.trim() || !ticket.email.trim()) return true;
    return attendeeFields.some((f) => f.required && !fieldValue(ticket.customFields, f).trim());
  };

  const addTicket = (type: TicketType) => {
    if (tickets.length >= 5) {
      toast({
        title: t('too_many_tickets_title'),
        variant: 'destructive',
        description: t('too_many_tickets_message'),
      });
      return;
    }
    const ticketTypeId = type.ticketTypeId;
    if (!ticketTypeId) return;
    setTickets((prev) => [
      ...prev,
      {
        email: buyerEmail,
        name: '',
        customFields: {},
        rowId: Math.random().toString(36).substring(7),
        ticketTypeId,
      },
    ]);
    setTimeout(() => {
      ticketsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }, 100);
  };

  const setTicketCount = (type: TicketType, next: number) => {
    const current = countForType(type);
    if (next > current) {
      const room = Math.min(next - current, 5 - tickets.length);
      for (let i = 0; i < room; i++) addTicket(type);
      if (next - current > room) {
        toast({
          title: 'Zu viele Tickets',
          variant: 'destructive',
          description: 'Du kannst maximal 5 Tickets auf einmal bestellen.',
        });
      }
    } else if (next < current) {
      // Drop the last `current - next` rows of this type. The counter is local
      // to the updater so it stays pure under StrictMode's double-invocation.
      setTickets((prev) => {
        let remaining = current - next;
        const result: TicketItem[] = [];
        for (let i = prev.length - 1; i >= 0; i--) {
          if (remaining > 0 && prev[i].ticketTypeId === type.ticketTypeId) {
            remaining--;
            continue;
          }
          result.unshift(prev[i]);
        }
        return result;
      });
    }
  };

  const updateTicket = (ticket: TicketItem) =>
    setTickets((prev) => prev.map((t) => (t.rowId === ticket.rowId ? ticket : t)));

  const deleteTicket = (ticket: TicketItem) =>
    setTickets((prev) => prev.filter((t) => t.rowId !== ticket.rowId));

  const onSubmit = async (formId = 'ticketForm') => {
    setError('');
    setAttempted(true);
    const form = document.getElementById(formId) as HTMLFormElement | null;
    // Native validation scrolls to + focuses the first missing required field.
    if (!form?.reportValidity()) return;
    if (!tickets.length) {
      setError(t('add_one_ticket'));
      return;
    }
    try {
      const orderBuyerEmail = context.syncBuyerEmailWithFirstTicket
        ? deriveBuyerEmail(tickets, buyerEmail)
        : buyerEmail;
      const result = await placeTicketOrder({
        owner: context.ownerInput,
        buyer: { email: orderBuyerEmail, customFields: buyerFields },
        tickets: tickets.map(({ ticketTypeId, name, email, customFields }) => ({
          ticketTypeId,
          name,
          email,
          customFields,
        })),
      });
      const orderId = result.data.order.orderId;
      navigate(context.orderPath ? context.orderPath(orderId) : `/orders/${orderId}`);
    } catch (e) {
      console.error('Error buying ticket', e);
      setError(t('order_failed'));
    }
  };

  return {
    ticketTypes,
    isLoading,
    tickets,
    ticketsWithType,
    totalPrice,
    summary,
    buyerEmail,
    setBuyerEmail,
    buyerFields,
    setBuyerFields,
    orderFields,
    attendeeFields,
    countForType,
    availableForType,
    ticketIncomplete,
    addTicket,
    setTicketCount,
    updateTicket,
    deleteTicket,
    onSubmit,
    isPending,
    error,
    attempted,
    ticketsRef,
  };
};
